import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import type { SalienceData } from '@/lib/demographics'
import { cn } from '@/lib/utils'

interface SalienceChartProps {
  data: SalienceData[]
  className?: string
}

const SALIENCE_COLORS = [
  '#3b82f6', // blue-500 (most important)
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#bfdbfe', // blue-200
  '#dbeafe', // blue-100
  '#eff6ff', // blue-50
  '#f3f4f6', // gray-100
]

export function SalienceChart({ data, className }: SalienceChartProps) {
  const sortedData = [...data].sort((a, b) => b.value - a.value)

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Issue Salience</CardTitle>
        <CardDescription>What voters care about most (population-weighted average)</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedData}
              layout="vertical"
              margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
            >
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis
                type="category"
                dataKey="label"
                width={100}
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload as SalienceData
                    return (
                      <div className="bg-popover text-popover-foreground rounded-lg border p-2 shadow-md">
                        <div className="font-medium">{item.label}</div>
                        <div className="text-muted-foreground text-sm">
                          Relative importance: {item.percentage}%
                        </div>
                        <div className="text-muted-foreground text-xs">
                          Raw weight: {item.value.toFixed(3)}
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Bar dataKey="percentage" radius={[0, 4, 4, 0]}>
                {sortedData.map((entry, index) => (
                  <Cell
                    key={entry.key}
                    fill={SALIENCE_COLORS[Math.min(index, SALIENCE_COLORS.length - 1)]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Text list for accessibility/clarity */}
        <div className="mt-4 space-y-2">
          {sortedData.slice(0, 3).map((item, index) => (
            <div key={item.key} className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm w-4">{index + 1}.</span>
              <span className="text-sm font-medium">{item.label}</span>
              <div className="flex-1 mx-2">
                <div className="bg-muted h-2 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: SALIENCE_COLORS[index],
                    }}
                  />
                </div>
              </div>
              <span className="text-muted-foreground text-sm tabular-nums w-12 text-right">
                {item.percentage}%
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
